/**
 * sentry-setup.js — Initialisation Sentry frontend (Lot Pré-bêta).
 *
 * Désactivé par défaut. Activé uniquement si REACT_APP_SENTRY_DSN est présent
 * dans /app/frontend/.env. Ne provoque aucun crash si la variable est absente.
 *
 * Filtrage des PII analogue au backend.
 */
let _initialized = false;

const PII_FIELDS = /^(.*email.*|.*phone.*|password|token|authorization|cookie|seller_first_name|seller_last_name|message|note|comment|address.*)$/i;
const EMAIL_RE = /[\w.-]+@[\w.-]+\.\w+/g;
const PHONE_RE = /\b(?:0|\+33\s?)[1-9](?:[ .\-]?\d{2}){4}\b/g;

function scrubValue(value) {
    if (typeof value !== 'string') return value;
    return value.replace(EMAIL_RE, '[email_redacted]').replace(PHONE_RE, '[phone_redacted]');
}

function scrubObject(obj) {
    if (!obj || typeof obj !== 'object') return obj;
    if (Array.isArray(obj)) return obj.map(scrubObject);
    const out = {};
    for (const k of Object.keys(obj)) {
        if (PII_FIELDS.test(k)) {
            out[k] = '[redacted]';
        } else if (typeof obj[k] === 'object' && obj[k] !== null) {
            out[k] = scrubObject(obj[k]);
        } else {
            out[k] = scrubValue(obj[k]);
        }
    }
    return out;
}

export async function initSentry() {
    if (_initialized) return false;
    const dsn = process.env.REACT_APP_SENTRY_DSN;
    if (!dsn) {
        // eslint-disable-next-line no-console
        console.info('[SENTRY] DSN absent → désactivé.');
        return false;
    }
    try {
        const Sentry = await import('@sentry/react');
        Sentry.init({
            dsn,
            environment: process.env.REACT_APP_SENTRY_ENV || 'preview',
            release: process.env.REACT_APP_SENTRY_RELEASE || undefined,
            tracesSampleRate: parseFloat(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || '0.1'),
            sendDefaultPii: false,
            beforeSend: (event) => {
                try {
                    if (event.request) event.request = scrubObject(event.request);
                    if (event.user) {
                        event.user.email = '[redacted]';
                        event.user.username = '[redacted]';
                        event.user.ip_address = undefined;
                    }
                    if (event.extra) event.extra = scrubObject(event.extra);
                    if (event.contexts) event.contexts = scrubObject(event.contexts);
                    if (event.breadcrumbs) {
                        event.breadcrumbs = event.breadcrumbs.map(bc => ({
                            ...bc,
                            data: bc.data ? scrubObject(bc.data) : bc.data,
                            message: bc.message ? scrubValue(bc.message) : bc.message,
                        }));
                    }
                } catch (e) {
                    // Ne pas empêcher l'envoi si le scrubbing échoue
                }
                return event;
            },
            ignoreErrors: [
                'ResizeObserver loop limit exceeded',
                'Non-Error promise rejection captured',
            ],
        });
        _initialized = true;
        // eslint-disable-next-line no-console
        console.info('[SENTRY] frontend initialisé.');
        return true;
    } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[SENTRY] frontend init failed', e);
        return false;
    }
}
